/**
 * Error utility functions for consistent error handling across the application
 */
import { LambdaResultError } from './lambdaResultUtils';

/**
 * Extracts error message from an unknown error type
 * @param error - The error to extract message from
 * @returns The error message string
 */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'Unknown error';
};

/**
 * Formats an API error with context for consistent error handling across services
 * @param context - Description of the operation that failed (e.g., "Failed to fetch reports configuration")
 * @param error - The error that occurred
 * @returns A new Error with formatted message, or a LambdaResultError unchanged
 *
 * @example
 * ```typescript
 * try {
 *   await fetchData();
 * } catch (error) {
 *   throw formatApiError('Failed to fetch reports configuration', error);
 * }
 * ```
 */
export const formatApiError = (context: string, error: unknown): Error => {
  // Already a user-facing backend message; keep it (and its warning/error level) unwrapped
  if (error instanceof LambdaResultError) {
    return error;
  }
  const message = getErrorMessage(error);
  return new Error(`${context}: ${message}`);
};
